// frontend/src/services/api.js
// Axios 基礎設定 - 統一管理所有 API 請求

import axios from 'axios';
import tokenManager from './tokenManager';

// 從環境變數讀取 API URL，預設為 localhost:5000
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// 建立 axios 實例
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // 30 秒超時（考慮到檔案上傳可能需要較長時間）
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // 重要：攜帶 Cookie（HttpOnly Refresh Token + CSRF Token）
  maxContentLength: 500 * 1024 * 1024, // 500MB - 支援大型備份檔案
  maxBodyLength: 500 * 1024 * 1024, // 500MB - 支援大型備份檔案
});

// 輔助函數：從 Cookie 讀取值
const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
};

// 請求攔截器 - 在發送請求前執行
api.interceptors.request.use(
  (config) => {
    // 使用 tokenManager 取得 token
    const token = tokenManager.getAccessToken();

    // 如果有 token，加入 Authorization header
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // 加入 CSRF Token（對於狀態變更請求）
    const csrfMethods = ['POST', 'PUT', 'DELETE', 'PATCH'];
    if (csrfMethods.includes(config.method?.toUpperCase())) {
      const csrfToken = getCookie('XSRF-TOKEN');
      if (csrfToken) {
        config.headers['X-CSRF-Token'] = csrfToken;
      }
    }

    console.log('[API] 發送請求:', config.method.toUpperCase(), config.url);
    return config;
  },
  (error) => {
    console.error('[API] 請求錯誤:', error);
    return Promise.reject(error);
  }
);

// 回應攔截器 - 在收到回應後執行
api.interceptors.response.use(
  (response) => {
    console.log('[API] 收到回應:', response.status, response.config.url);
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // 統一錯誤處理
    if (error.response) {
      const status = error.response.status;
      const message = error.response.data?.message || error.response.data?.error || '發生錯誤';

      console.error(`[API] 錯誤 [${status}]:`, message);

      // 處理 401 未授權錯誤
      if (status === 401 && !originalRequest._retry) {
        // 標記此請求已經重試過，避免無限循環
        originalRequest._retry = true;

        // 檢查是否為刷新 Token 請求本身（避免無限循環）
        if (originalRequest.url?.includes('/auth/refresh')) {
          console.error('[API] Token 刷新請求失敗，導向登入頁');
          tokenManager.clearTokens();
          window.location.href = '/login';
          return Promise.reject(error);
        }

        // 重要：FormData 請求無法重試（數據已被消費）
        // 應該在 uploadService 中預先檢查並刷新 Token
        const isFormDataRequest = originalRequest.data instanceof FormData;
        if (isFormDataRequest) {
          console.error('[API] FormData 請求遇到 401，無法自動重試');
          console.error('[API] 提示：上傳服務應在請求前先檢查 Token');
          error.userMessage = '上傳失敗：登入已過期，請重新整理頁面後再試';
          return Promise.reject(error);
        }

        // Refresh Token 現在在 HttpOnly Cookie 中，由瀏覽器自動管理
        // 只要可能有 Token 就嘗試刷新
        if (!tokenManager.hasRefreshToken()) {
          console.warn('[API] 沒有 Refresh Token，需要重新登入');
          tokenManager.clearTokens();
          window.location.href = '/login';
          return Promise.reject(error);
        }

        try {
          console.log('[API] Token 已過期，嘗試刷新...');

          // 使用 tokenManager 刷新 Token
          const newToken = await tokenManager.refreshAccessToken();

          console.log('[API] Token 刷新成功，重試原始請求');

          // 用新 Token 更新原始請求的 header
          originalRequest.headers.Authorization = `Bearer ${newToken}`;

          // 重試原始請求
          return api.request(originalRequest);
        } catch (refreshError) {
          console.error('[API] Token 刷新失敗:', refreshError);
          // tokenManager.refreshAccessToken() 失敗時會自動清除 Token 並導向登入頁
          return Promise.reject(refreshError);
        }
      }

      // 其他狀態碼處理
      switch (status) {
        case 403:
          console.warn('[API] 權限不足');
          break;
        case 404:
          console.warn('[API] 找不到資源');
          break;
        case 413:
          console.warn('[API] 檔案大小超過限制');
          break;
        case 500:
          console.error('[API] 伺服器錯誤');
          break;
        default:
          break;
      }

      // 將錯誤訊息加入 error 物件
      error.userMessage = message;
    } else if (error.request) {
      // 請求已發送但沒有收到回應
      console.error('[API] 無法連線到伺服器');
      error.userMessage = '無法連線到伺服器，請檢查網路連線';
    } else {
      // 其他錯誤
      console.error('[API] 請求設定錯誤:', error.message);
      error.userMessage = '請求失敗：' + error.message;
    }

    return Promise.reject(error);
  }
);

export default api;
